import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Plus,
  Trash,
  Pencil,
  DotsThree,
  MagnifyingGlass,
  TreeStructure,
  Tag,
} from '@phosphor-icons/react';
import {
  Segment,
  SEGMENT_LABEL_CONFIG,
} from 'shared/types/segment';
import {
  useSegments,
  useDeleteSegment,
} from 'hooks/useSegments';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const SegmentsList: React.FC = () => {
  const navigate = useNavigate();
  const { segments, isLoading, refetch } = useSegments();
  const { deleteSegment, isDeleting } = useDeleteSegment();

  const [deleteConfirmSegment, setDeleteConfirmSegment] = useState<Segment | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  // Filter segments
  const filteredSegments = useMemo(() => {
    let result = segments;
    if (selectedLabel) {
      result = result.filter((s) => s.labels?.includes(selectedLabel));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.description?.toLowerCase().includes(query),
      );
    }
    return result;
  }, [segments, searchQuery, selectedLabel]);

  // Labels in use (for filter bar)
  const labelsInUse = useMemo(() => {
    const labelSet = new Set(segments.flatMap((s) => s.labels || []));
    return Array.from(labelSet);
  }, [segments]);

  const handleDelete = async () => {
    if (!deleteConfirmSegment) return;
    try {
      await deleteSegment(deleteConfirmSegment.id);
      toast.success('Segment succesvol verwijderd');
      setDeleteConfirmSegment(null);
      await refetch();
    } catch (error) {
      console.error('Error deleting segment:', error);
      toast.error('Kon segment niet verwijderen');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-5 w-5" />
        <span className="ml-2 text-sm text-muted-foreground">Segmenten laden...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Segmenten</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Definieer doelgroepen met booleaanse expressies over clusters
            </p>
          </div>
          <Button
            onClick={() => navigate('/segments/new')}
          >
            <Plus className="h-4 w-4 mr-1" />
            Nieuw segment
          </Button>
        </div>
      </div>

      {/* Search bar - only when segments exist */}
      {segments.length > 0 && (
        <div className="mb-4">
          <div className="relative w-full sm:w-80">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Zoek segmenten..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      )}

      {/* Label Filter Bar */}
      {labelsInUse.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <Tag className="size-4 text-muted-foreground" weight="duotone" />
          <Badge
            variant={selectedLabel === null ? 'default' : 'outline'}
            className={`cursor-pointer ${selectedLabel === null ? 'bg-brick-primary' : ''}`}
            onClick={() => setSelectedLabel(null)}
          >
            Alle
          </Badge>
          {labelsInUse.map((label) => {
            const config = SEGMENT_LABEL_CONFIG[label];
            const isActive = selectedLabel === label;
            return (
              <Badge
                key={label}
                variant={isActive ? 'default' : 'outline'}
                className={`cursor-pointer ${isActive ? (config?.color || '') : ''}`}
                onClick={() => setSelectedLabel(isActive ? null : label)}
              >
                {config?.label || label}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {segments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <TreeStructure size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="font-medium text-sm text-foreground mb-1">
            Nog geen segmenten aangemaakt
          </p>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Definieer doelgroepen door clusters te combineren met EN/OF logica.
            Ideaal voor onderhoud, regelgeving, of communicatie.
          </p>
        </div>
      ) : filteredSegments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <MagnifyingGlass size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="font-medium text-sm text-foreground mb-1">
            Geen segmenten gevonden
            {searchQuery && ` voor "${searchQuery}"`}
            {selectedLabel && ` met label "${SEGMENT_LABEL_CONFIG[selectedLabel]?.label || selectedLabel}"`}
          </p>
          <Button
            variant="link"
            onClick={() => {
              setSearchQuery('');
              setSelectedLabel(null);
            }}
            className="mt-1 text-sm"
          >
            Filters wissen
          </Button>
        </div>
      ) : (
        /* Segments Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredSegments.map((segment) => (
            <Card
              key={segment.id}
              className="group overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/segments/${segment.id}`)}
            >
              <CardHeader className="pt-4 pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="flex items-center gap-2">
                      <TreeStructure
                        className="size-4 text-brick-primary shrink-0"
                        weight="duotone"
                      />
                      <CardTitle className="text-base font-semibold truncate">
                        {segment.name}
                      </CardTitle>
                      {segment.active ? (
                        <Badge
                          variant="secondary"
                          className="bg-moss-secondary-light text-moss-primary text-[10px] px-1.5 py-0 h-5 shrink-0"
                        >
                          Actief
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 h-5 shrink-0"
                        >
                          Inactief
                        </Badge>
                      )}
                    </div>
                    {segment.description && (
                      <CardDescription className="line-clamp-2 mt-1">
                        {segment.description}
                      </CardDescription>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <DotsThree className="size-5" weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenuItem
                        onClick={() => navigate(`/segments/${segment.id}`)}
                      >
                        <Pencil className="size-4" />
                        <span>Bewerken</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteConfirmSegment(segment)}
                        className="text-destructive focus:text-destructive focus:bg-destructive/10"
                      >
                        <Trash className="size-4" />
                        <span>Verwijderen</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>

              <CardContent className="pt-0 pb-3 space-y-2">
                {/* Labels */}
                {segment.labels && segment.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {segment.labels.map((label) => {
                      const config = SEGMENT_LABEL_CONFIG[label];
                      return (
                        <Badge
                          key={label}
                          variant="secondary"
                          className={`text-xs ${config?.color || ''}`}
                        >
                          {config?.label || label}
                        </Badge>
                      );
                    })}
                  </div>
                )}

                {/* Cluster names */}
                {segment.clusterNames && segment.clusterNames.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] text-muted-foreground/70">
                      Samengesteld uit volgende clusters:
                    </span>
                    {segment.clusterNames.map((clusterName) => (
                      <Badge
                        key={clusterName}
                        variant="outline"
                        className="text-xs text-muted-foreground"
                      >
                        {clusterName}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Unit count */}
                <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-muted/50 border border-border/50">
                  <TreeStructure
                    className="size-3.5 text-brick-primary shrink-0"
                    weight="duotone"
                  />
                  <span className="text-xs text-muted-foreground">
                    {segment.unitCount !== undefined
                      ? `${segment.unitCount} ${segment.unitCount === 1 ? 'unit' : 'units'}`
                      : 'Units niet berekend'}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteConfirmSegment}
        onOpenChange={() => setDeleteConfirmSegment(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Segment verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je het segment &quot;{deleteConfirmSegment?.name}&quot;
              wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Spinner className="size-4" />}
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SegmentsList;
